import React, { useState, useEffect } from "react";
import { SidebarNav } from "@/components/ui/sidebar-nav";
import { useDensity } from "@/hooks/use-density";
import { Toaster } from "@/components/ui/toaster";
import { BottomSettingsBar } from "@/components/ui/bottom-settings-bar";
import { SidebarProvider, Sidebar, SidebarContent, SidebarTrigger } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PWAInstallButton, PWAUpdateNotification, PWAMobileInstructions } from "@/components/pwa-install-button";
import QuickReminderFab from "@/components/reminders/QuickReminderFab";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

// BottomQuickReminder integrované do BottomSettingsBar
import { cn } from "@/lib/utils";

interface MainLayoutProps {
  children: React.ReactNode;
  className?: string;
}

export function MainLayout({ children, className }: MainLayoutProps) {
  const [fabEnabled, setFabEnabled] = useState(true);
  useDensity(); // Initialize density and apply class to root

  // Load FAB setting from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('reminder-fab-enabled');
    if (saved !== null) {
      setFabEnabled(JSON.parse(saved));
    }
  }, []);

  // Načítanie pozadia pre dark a light theme
  const { data: publicSettings = [] } = useQuery({
    queryKey: ['/api/system-settings/public'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/system-settings/public');
      return response.json();
    },
    staleTime: 60 * 1000, // 1 min - umožní refetch po zmene ikony
    refetchOnWindowFocus: true,
  });

  // Aplikovanie pozadia na CSS variables
  useEffect(() => {
    const getSetting = (key: string): string => {
      const setting = publicSettings.find((s: any) => s.key === key);
      let value = setting?.value || '';
      // Odstránime úvodzovky ak tam sú (napr. "/uploads/..." -> /uploads/...)
      if (value && typeof value === 'string') {
        value = value.trim();
        if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1);
        }
      }
      return value;
    };

    const lightBg = getSetting('ui.background.light');
    const darkBg = getSetting('ui.background.dark');

    console.log('🎨 [Background] Settings loaded:', { lightBg, darkBg, settingsCount: publicSettings.length });

    const root = document.documentElement;
    const body = document.body;
    
    // Získame aktuálnu tému
    const isDark = root.classList.contains('dark');
    
    console.log('🎨 [Background] Current theme:', isDark ? 'dark' : 'light');
    
    // Nastavíme pozadie podľa aktuálnej témy
    if (isDark) {
      // Dark theme - použijeme dark background ak existuje
      if (darkBg && darkBg.trim() !== '') {
        root.style.setProperty('--background-image-dark', `url('${darkBg}')`);
        console.log('🎨 [Background] Applying dark background:', darkBg);
        body.style.setProperty('background-color', 'transparent', 'important');
        body.style.setProperty('background-image', `url('${darkBg}')`, 'important');
        body.style.setProperty('background-repeat', 'no-repeat', 'important');
        /* background-size/position rieši index.css (aj mobilná media query) */
        /* background-attachment rieši index.css (fixed desktop, scroll mobil/tablet) */
      } else {
        root.style.setProperty('--background-image-dark', 'none');
        body.style.setProperty('background-image', 'none', 'important');
        body.style.setProperty('background-color', '', 'important');
      }
      // Nastavíme aj light (pre CSS variables), ale neaplikujeme ho
      root.style.setProperty('--background-image-light', lightBg && lightBg.trim() !== '' ? `url('${lightBg}')` : 'none');
    } else {
      // Light theme - použijeme light background ak existuje
      if (lightBg && lightBg.trim() !== '') {
        root.style.setProperty('--background-image-light', `url('${lightBg}')`);
        console.log('🎨 [Background] Applying light background:', lightBg);
        body.style.setProperty('background-color', 'transparent', 'important');
        body.style.setProperty('background-image', `url('${lightBg}')`, 'important');
        body.style.setProperty('background-repeat', 'no-repeat', 'important');
        /* background-size/position rieši index.css (aj mobilná media query) */
        /* background-attachment rieši index.css (fixed desktop, scroll mobil/tablet) */
      } else {
        root.style.setProperty('--background-image-light', 'none');
        body.style.setProperty('background-image', 'none', 'important');
        body.style.setProperty('background-color', '', 'important');
      }
      // Nastavíme aj dark (pre CSS variables), ale neaplikujeme ho
      root.style.setProperty('--background-image-dark', darkBg && darkBg.trim() !== '' ? `url('${darkBg}')` : 'none');
    }

    // Ikona aplikácie (favicon) – v záložke prehliadača a pri PWA
    const faviconUrl = getSetting('site.favicon');
    if (faviconUrl && typeof document !== 'undefined') {
      const type = faviconUrl.toLowerCase().endsWith('.ico') ? 'image/x-icon' : 'image/png';
      for (const rel of ['icon', 'shortcut icon']) {
        let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = rel;
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
        link.type = type;
      }
      let appleTouch = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
      if (!appleTouch) {
        appleTouch = document.createElement('link');
        appleTouch.rel = 'apple-touch-icon';
        document.head.appendChild(appleTouch);
      }
      appleTouch.href = faviconUrl;
    }
  }, [publicSettings]);

  // Sledovanie zmien témy pre prepnutie pozadia
  useEffect(() => {
    const getCurrentBackgrounds = () => {
      const root = document.documentElement;
      const lightBg = root.style.getPropertyValue('--background-image-light') || '';
      const darkBg = root.style.getPropertyValue('--background-image-dark') || '';
      return { lightBg, darkBg };
    };

    const applyBackgroundForTheme = () => {
      const root = document.documentElement;
      const body = document.body;
      const isDark = root.classList.contains('dark');
      
      const { lightBg, darkBg } = getCurrentBackgrounds();
      
      if (isDark && darkBg && darkBg !== 'none') {
        const url = darkBg.replace(/url\(['"]?(.+?)['"]?\)/, '$1').trim();
        console.log('🎨 [Theme Change] Applying dark background:', url);
        body.style.setProperty('background-color', 'transparent', 'important');
        body.style.setProperty('background-image', `url('${url}')`, 'important');
        body.style.setProperty('background-repeat', 'no-repeat', 'important');
        /* background-size/position rieši index.css */
      } else if (!isDark && lightBg && lightBg !== 'none') {
        const url = lightBg.replace(/url\(['"]?(.+?)['"]?\)/, '$1').trim();
        console.log('🎨 [Theme Change] Applying light background:', url);
        body.style.setProperty('background-color', 'transparent', 'important');
        body.style.setProperty('background-image', `url('${url}')`, 'important');
        body.style.setProperty('background-repeat', 'no-repeat', 'important');
        /* background-size/position rieši index.css */
      } else {
        body.style.setProperty('background-image', 'none', 'important');
        body.style.setProperty('background-color', '', 'important');
      }
    };

    const observer = new MutationObserver(() => {
      // Ak sa zmení theme, aktualizujeme pozadie
      applyBackgroundForTheme();
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className="flex flex-col md:flex-row w-full min-w-0 min-h-screen text-foreground overflow-x-hidden" style={{ backgroundColor: 'transparent' }}>
          {/* Desktop collapsible sidebar */}
          <div className="hidden md:block">
            <Sidebar collapsible="icon">
              <SidebarNav />
            </Sidebar>
          </div>
          
          {/* Mobile header with navigation and PWA safe area */}
          <div className="block md:hidden sticky top-0 z-40 w-full border-b pwa-header" style={{ backgroundColor: 'transparent' }}>
            <SidebarNav isMobile={true} />
          </div>
          
          {/* Main content with PWA safe area spacing - min-w-0 prevents flex overflow */}
          <main className={cn(
            "flex-1 min-w-0 w-full overflow-y-auto overflow-x-hidden",
            "md:px-4 md:pt-6 lg:px-8", // Desktop padding
            "pwa-main-content", // PWA mobile spacing
            "text-foreground", // Use theme colors
            className
          )}
          style={{ backgroundColor: 'transparent' }}
          >
            {/* Sidebar toggle button for desktop */}
            <div className="hidden md:block mb-4">
              <SidebarTrigger />
            </div>
            
            {children}
          </main>
          
          {/* Unified bottom bar with PWA safe area */}
          <div className="fixed bottom-0 left-0 right-0 z-50 pwa-bottom-nav">
            <BottomSettingsBar />
          </div>
          
          {/* PWA Install Button in bottom bar - hidden on mobile to avoid menu overlap */}
          <div className="hidden md:block fixed bottom-20 right-4 z-40">
            <PWAInstallButton variant="default" size="sm" />
          </div>
          
          {/* PWA Update Notification */}
          <PWAUpdateNotification />
          
          <Toaster />
          
          {/* Quick Reminder FAB with safe area consideration */}
          <QuickReminderFab 
            isVisible={fabEnabled} 
            onToggle={() => setFabEnabled(!fabEnabled)} 
          />
        </div>
      </SidebarProvider>
    </TooltipProvider>
  );
}